// Green Gold — bilingual content (EN / ES). Text sourced from the official brand brief & pitch deck.

export const LOGO_URL = "https://customer-assets-7cd3h4nn.emergentagent.net/job_3a311a38-8d2a-4e5a-b93d-d2004eaff5a2/artifacts/fk8xqpdu_LOGO_GrreenGold_v1.webp";

export const IMAGES = {
  heroLeaves: "https://images.unsplash.com/photo-1598559213718-da4a925fbf71?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  farm: "https://images.unsplash.com/photo-1698764700248-c3798485f6f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  premium: "https://images.unsplash.com/photo-1573828235229-fb27fdc8da91?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  standard: "https://images.unsplash.com/photo-1739664237734-e6e9b91bfd0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  port: "https://images.pexels.com/photos/16229885/pexels-photo-16229885.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800",
};

export const CONTENT = {
  en: {
    nav: [
      { label: "Origin", href: "#manifesto" },
      { label: "Products", href: "#products" },
      { label: "Difference", href: "#difference" },
      { label: "Model", href: "#model" },
      { label: "Contact", href: "#contact" },
    ],
    navCta: "Request a Quote",
    hero: {
      eyebrow: "Premium Venezuelan Plantains · For International Markets",
      lines: ["The Return of", "Venezuelan Agricultural", "Excellence"],
      paragraph:
        "Green Gold reintroduces a historically recognized origin — valued for quality, size, and flavor — under modern export standards. We compete through differentiation, not price.",
      cta1: "Explore the Lines",
      cta2: "Become a Buyer",
      experience: { value: "20+", label: "Years of experience in this trade" },
    },
    stats: [
      { value: "20+", label: "Years in this trade" },
      { value: "850", label: "Boxes per 40HQ reefer" },
      { value: "90–95%", label: "Exportable yield" },
      { value: "7–9", label: "Days maritime transit" },
    ],
    marquee: [
      "Calidad que genera confianza",
      "Quality that builds trust",
      "The true flavor of the Venezuelan plantain",
      "Origin · Quality · Flavor · Consistency",
    ],
    manifesto: {
      eyebrow: "The Origin",
      headline: [
        { text: "Not just to export" },
        { text: "plantains —" },
        { text: "to rebuild an opportunity.", em: true },
      ],
      intro:
        "Green Gold was not created to export one container. It was created to rebuild an opportunity — for producers, for buyers, and for Venezuela.",
      captionTitle: "Sur del Lago · Barinas",
      captionSub: "Two strategic origins",
      values: ["Quality", "Transparency", "Reliability", "Development", "Excellence"],
      chapters: [
        { no: "01", title: "A Reintroduction, Not an Introduction", body: "Venezuela is not an unknown origin. It is a historically recognized origin — valued for quality, size, and flavor — whose presence in international markets declined as trade contracted. Green Gold exists to reintroduce that established offer." },
        { no: "02", title: "Demand Precedes Supply", body: "Unlike ventures that produce first and then search for buyers, Green Gold begins with aligned buyers and more than twenty years of prior experience in this same trade, and builds supply around identified demand. This reduces commercial risk and accelerates validation." },
        { no: "03", title: "Quality Is an Obligation", body: "Fruit that does not meet export standards is never shipped under the Green Gold brand — because long-term customer trust is worth more than one additional box. Brand reputation always outweighs selling one more container." },
        { no: "04", title: "An Institutional Ambition", body: "The objective is not a trading operation but a durable, progressively integrated agricultural export platform — connecting producers, financing, logistics, and international commercialization." },
      ],
    },
    products: {
      eyebrow: "Product Architecture",
      heading: { pre: "Two Lines, ", em: "One Standard" },
      subcopy: "Origin establishes positioning — quality determines the final product. Acceptance under either line always depends on meeting export standards.",
      originLabel: "Origin · ",
      customerLabel: "Ideal customer — ",
      items: [
        { id: "premium", image: "premium", accent: "gold", badge: "Premium", name: "Green Gold Premium™", origin: "Sur del Lago de Maracaibo", positioning: "Flagship line — high Brix, superior flavor, premium differentiation.", customer: "Specialized importers and premium distributors seeking margin through differentiation.", quote: "The true flavor of the Venezuelan plantain." },
        { id: "standard", image: "standard", accent: "leaf", badge: "Volume", name: "Green Gold Standard™", origin: "Barinas", positioning: "High-volume line — great size, excellent presentation, commercial competitiveness.", customer: "Volume importers and traditional distributors in the market's main commercial segment.", quote: "Great size. Great presence. Great flavor. Great opportunity." },
      ],
    },
    packaging: {
      eyebrow: "The Export Box",
      heading: { pre: "Packed with ", em: "Pride" },
      subcopy: "Every Green Gold box carries our mark across the world — premium Venezuelan plantains from the fertile lands of the Sur del Lago region, graded, cooled, and sealed for the journey.",
      specs: [
        { label: "Net weight", value: "50 lbs · 22.68 kg" },
        { label: "Grade", value: "Extra Large · Premium Quality" },
        { label: "Keep at", value: "13°C / 55°F" },
        { label: "Origin", value: "Sur del Lago de Maracaibo" },
      ],
    },
    difference: {
      eyebrow: "Competitive Differentiation",
      heading: { pre: "Six Pillars ", em: "Beyond Price" },
      landscape: "Guatemala leads on volume · Colombia carries a premium history with recurring delivery difficulties · Ecuador competes on economy · Costa Rica offers stable quality at higher cost.",
      pillars: [
        { icon: "MapPin", title: "Origin", body: "Venezuela's authentic agricultural history, anchored in two distinct producing regions." },
        { icon: "Scale", title: "Measurable Quality", body: "Brix, size, appearance, uniformity, export condition — objective criteria, not claims." },
        { icon: "Sparkles", title: "Flavor", body: "Differentiation grounded in consumption experience, comparatively evaluated." },
        { icon: "RefreshCw", title: "Consistency", body: "The same standard repeated, operation after operation. Trust is built through repetition." },
        { icon: "Handshake", title: "Direct Relationships", body: "Fewer intermediaries — greater control, greater transparency." },
        { icon: "ShieldCheck", title: "Brand", body: "Most traded plantains are commodities. Green Gold builds a brand recognized for quality, origin, and trust." },
      ],
    },
    process: {
      eyebrow: "Business Model",
      heading: { pre: "Direct, Disciplined, ", em: "Pre-Sold" },
      subcopy: "Minimal handling, maximum rotation. Containers are pre-sold before arrival — reducing inventory exposure and working-capital requirements. Every handling operation adds risk, so fruit is moved as little as possible to protect export quality.",
      targetLabel: "Target customers —",
      targets: ["Importers", "Distributors", "Wholesalers", "Food service", "Specialized chains"],
      steps: [
        { icon: "Sprout", title: "Production", place: "Venezuela", body: "Harvest, packing, technical testing, quality control." },
        { icon: "Package", title: "Selection & Packing", place: "Venezuela", body: "Refractometry, ripening logs, export-condition grading." },
        { icon: "Snowflake", title: "Cold Chain", place: "In transit", body: "Temperature monitoring and traceability as critical priorities." },
        { icon: "Ship", title: "Maritime Export", place: "40HQ reefer", body: "Minimal maritime transit and handling." },
        { icon: "Warehouse", title: "Miami Reception", place: "United States", body: "Reception, supervision, invoicing, and collections." },
        { icon: "Truck", title: "Distributor Delivery", place: "Direct", body: "Direct delivery to wholesale distributors — no cross-docking." },
      ],
    },
    execution: {
      eyebrow: "Execution Readiness",
      heading: { pre: "Specified, Sequenced, ", em: "Risk-Aware" },
      logisticsLabel: "Defined Logistics",
      logistics: [
        { k: "Primary transport", v: "40HQ maritime reefer" },
        { k: "Capacity", v: "850 boxes / container" },
        { k: "Box weight", v: "22.7 kg net · 23 kg gross" },
        { k: "Container weight", v: "19,295 kg net · 21,250 kg gross" },
      ],
      disciplineLabel: "Operating Discipline",
      discipline: [
        "Brand reputation always outweighs selling one additional box.",
        "Cold chain, temperature monitoring, and traceability treated as critical priorities.",
        "Technical validation: ripening logs, refractometry (Brix), refrigerated-stability testing.",
        "Volume increases only after consistent operational performance.",
        "KPIs across production, quality, logistics, commercial, and financial dimensions.",
      ],
      riskLabel: "Risk Awareness",
      risk: "An explicit risk register covers fruit availability, quality variability, logistics costs, working capital, transport dependence, and premature scaling — growth is deliberately paced to protect quality and customer confidence.",
    },
    roadmap: {
      eyebrow: "Growth Roadmap",
      heading: { pre: "From Validation to ", em: "Vertical Integration" },
      subcopy: "A long-term vision: an integrated agricultural platform — expandable to mango, cacao, limes, and avocados under the same architecture.",
      phases: [
        { no: "01", title: "Commercial Validation", body: "1 reefer / week — validate demand, logistics, profitability." },
        { no: "02", title: "Progressive Scaling", body: "Toward a stable base of 5 reefers / week, consolidating toward 10." },
        { no: "03", title: "Supply Control", body: "Supply contracts, exclusivity, strategic producer relationships." },
        { no: "04", title: "Agricultural Financing", body: "Selective producer financing and harvest commitments." },
        { no: "05", title: "Vertical Integration", body: "Own production and infrastructure — full value-chain control." },
      ],
    },
    contact: {
      eyebrow: "Open a Conversation",
      heading: { pre: "Let's build ", em: "confidence." },
      subcopy: "We welcome inquiries from importers, distributors, and investors. The most valuable commercial relationships are built through quality, consistency, and fulfillment.",
      directorsLabel: "Executive Directors",
      directors: ["Alicia Rufino", "Raul Barrios"],
      marketsLabel: "Markets",
      markets: "United States · Caribbean · Central America · Europe",
      emailLabel: "Email",
      email: "info@greengold.export",
      iamLabel: "I am a",
      types: [
        { id: "buyer", label: "Buyer" },
        { id: "investor", label: "Investor" },
        { id: "producer", label: "Producer" },
        { id: "other", label: "Other" },
      ],
      fields: { name: "Full name", email: "Email", company: "Company", country: "Country", message: "Message" },
      send: "Send Inquiry",
      sending: "Sending…",
      received: "Received",
      toastSuccess: "Inquiry received. Our team will be in touch shortly.",
      toastError: "Something went wrong. Please try again.",
    },
    footer: {
      desc: "Premium Venezuelan plantains for international markets. Reintroducing an origin of excellence under modern export standards.",
      navLabel: "Navigate",
      originsLabel: "Origins",
      origins: ["Sur del Lago de Maracaibo", "Barinas"],
      tagline: "Calidad que genera confianza.",
      rights: "All rights reserved.",
      subtag: "Quality that builds trust.",
    },
  },

  es: {
    nav: [
      { label: "Origen", href: "#manifesto" },
      { label: "Productos", href: "#products" },
      { label: "Diferencia", href: "#difference" },
      { label: "Modelo", href: "#model" },
      { label: "Contacto", href: "#contact" },
    ],
    navCta: "Solicitar Cotización",
    hero: {
      eyebrow: "Plátanos Venezolanos Premium · Para Mercados Internacionales",
      lines: ["El Regreso de la", "Excelencia Agrícola", "Venezolana"],
      paragraph:
        "Green Gold reintroduce un origen históricamente reconocido — valorado por su calidad, tamaño y sabor — bajo estándares modernos de exportación. Competimos por diferenciación, no por precio.",
      cta1: "Explora las Líneas",
      cta2: "Conviértete en Comprador",
      experience: { value: "20+", label: "Años de experiencia en este negocio" },
    },
    stats: [
      { value: "20+", label: "Años en este negocio" },
      { value: "850", label: "Cajas por contenedor 40HQ" },
      { value: "90–95%", label: "Rendimiento exportable" },
      { value: "7–9", label: "Días de tránsito marítimo" },
    ],
    marquee: [
      "Calidad que genera confianza",
      "El verdadero sabor del plátano venezolano",
      "Origen · Calidad · Sabor · Consistencia",
      "Confianza construida caja tras caja",
    ],
    manifesto: {
      eyebrow: "El Origen",
      headline: [
        { text: "No solo para exportar" },
        { text: "plátanos —" },
        { text: "sino para reconstruir una oportunidad.", em: true },
      ],
      intro:
        "Green Gold no fue creada para exportar un contenedor. Fue creada para reconstruir una oportunidad — para los productores, para los compradores y para Venezuela.",
      captionTitle: "Sur del Lago · Barinas",
      captionSub: "Dos orígenes estratégicos",
      values: ["Calidad", "Transparencia", "Confiabilidad", "Desarrollo", "Excelencia"],
      chapters: [
        { no: "01", title: "Una Reintroducción, No una Introducción", body: "Venezuela no es un origen desconocido. Es un origen históricamente reconocido — valorado por su calidad, tamaño y sabor — cuya presencia en los mercados internacionales disminuyó al contraerse el comercio. Green Gold existe para reintroducir esa oferta ya establecida." },
        { no: "02", title: "La Demanda Precede a la Oferta", body: "A diferencia de los proyectos que producen primero y luego buscan compradores, Green Gold comienza con compradores alineados y más de veinte años de experiencia previa en este mismo negocio, y construye la oferta en torno a una demanda identificada. Esto reduce el riesgo comercial y acelera la validación." },
        { no: "03", title: "La Calidad Es una Obligación", body: "La fruta que no cumple los estándares de exportación nunca se envía bajo la marca Green Gold — porque la confianza del cliente a largo plazo vale más que una caja adicional. La reputación de la marca siempre pesa más que vender un contenedor más." },
        { no: "04", title: "Una Ambición Institucional", body: "El objetivo no es una operación comercial sino una plataforma de exportación agrícola duradera y progresivamente integrada — que conecte a productores, financiamiento, logística y comercialización internacional." },
      ],
    },
    products: {
      eyebrow: "Arquitectura de Producto",
      heading: { pre: "Dos Líneas, ", em: "Un Estándar" },
      subcopy: "El origen establece el posicionamiento — la calidad determina el producto final. La aceptación bajo cualquier línea siempre depende de cumplir los estándares de exportación.",
      originLabel: "Origen · ",
      customerLabel: "Cliente ideal — ",
      items: [
        { id: "premium", image: "premium", accent: "gold", badge: "Premium", name: "Green Gold Premium™", origin: "Sur del Lago de Maracaibo", positioning: "Línea insignia — alto Brix, sabor superior, diferenciación premium.", customer: "Importadores especializados y distribuidores premium que buscan margen a través de la diferenciación.", quote: "El verdadero sabor del plátano venezolano." },
        { id: "standard", image: "standard", accent: "leaf", badge: "Volumen", name: "Green Gold Standard™", origin: "Barinas", positioning: "Línea de alto volumen — gran tamaño, excelente presentación, competitividad comercial.", customer: "Importadores de volumen y distribuidores tradicionales en el principal segmento comercial del mercado.", quote: "Gran tamaño. Gran presencia. Gran sabor. Gran oportunidad." },
      ],
    },
    packaging: {
      eyebrow: "La Caja de Exportación",
      heading: { pre: "Empacado con ", em: "Orgullo" },
      subcopy: "Cada caja de Green Gold lleva nuestra marca por el mundo — plátanos venezolanos premium de las fértiles tierras de la región Sur del Lago, seleccionados, refrigerados y sellados para el viaje.",
      specs: [
        { label: "Peso neto", value: "50 lbs · 22,68 kg" },
        { label: "Grado", value: "Tamaño Extra Grande · Calidad Premium" },
        { label: "Conservar a", value: "13°C / 55°F" },
        { label: "Origen", value: "Sur del Lago de Maracaibo" },
      ],
    },
    difference: {
      eyebrow: "Diferenciación Competitiva",
      heading: { pre: "Seis Pilares ", em: "Más Allá del Precio" },
      landscape: "Guatemala lidera en volumen · Colombia tiene una historia premium con recurrentes dificultades de entrega · Ecuador compite en economía · Costa Rica ofrece calidad estable a mayor costo.",
      pillars: [
        { icon: "MapPin", title: "Origen", body: "La auténtica historia agrícola de Venezuela, anclada en dos regiones productoras distintas." },
        { icon: "Scale", title: "Calidad Medible", body: "Brix, tamaño, apariencia, uniformidad, condición de exportación — criterios objetivos, no promesas." },
        { icon: "Sparkles", title: "Sabor", body: "Diferenciación basada en la experiencia de consumo, evaluada de forma comparativa." },
        { icon: "RefreshCw", title: "Consistencia", body: "El mismo estándar repetido, operación tras operación. La confianza se construye con la repetición." },
        { icon: "Handshake", title: "Relaciones Directas", body: "Menos intermediarios — mayor control, mayor transparencia." },
        { icon: "ShieldCheck", title: "Marca", body: "La mayoría de los plátanos comercializados son commodities. Green Gold construye una marca reconocida por calidad, origen y confianza." },
      ],
    },
    process: {
      eyebrow: "Modelo de Negocio",
      heading: { pre: "Directo, Disciplinado, ", em: "Pre-Vendido" },
      subcopy: "Mínima manipulación, máxima rotación. Los contenedores se venden antes de su llegada — reduciendo la exposición de inventario y las necesidades de capital de trabajo. Cada manipulación añade riesgo, por lo que la fruta se mueve lo menos posible para proteger la calidad de exportación.",
      targetLabel: "Clientes objetivo —",
      targets: ["Importadores", "Distribuidores", "Mayoristas", "Food service", "Cadenas especializadas"],
      steps: [
        { icon: "Sprout", title: "Producción", place: "Venezuela", body: "Cosecha, empaque, pruebas técnicas, control de calidad." },
        { icon: "Package", title: "Selección y Empaque", place: "Venezuela", body: "Refractometría, registros de maduración, clasificación por condición de exportación." },
        { icon: "Snowflake", title: "Cadena de Frío", place: "En tránsito", body: "Monitoreo de temperatura y trazabilidad como prioridades críticas." },
        { icon: "Ship", title: "Exportación Marítima", place: "Reefer 40HQ", body: "Mínimo tránsito marítimo y manipulación." },
        { icon: "Warehouse", title: "Recepción en Miami", place: "Estados Unidos", body: "Recepción, supervisión, facturación y cobranzas." },
        { icon: "Truck", title: "Entrega a Distribuidores", place: "Directa", body: "Entrega directa a distribuidores mayoristas — sin cross-docking." },
      ],
    },
    execution: {
      eyebrow: "Preparación para Ejecutar",
      heading: { pre: "Especificado, Secuenciado, ", em: "Consciente del Riesgo" },
      logisticsLabel: "Logística Definida",
      logistics: [
        { k: "Transporte principal", v: "Reefer marítimo 40HQ" },
        { k: "Capacidad", v: "850 cajas / contenedor" },
        { k: "Peso por caja", v: "22,7 kg neto · 23 kg bruto" },
        { k: "Peso del contenedor", v: "19.295 kg neto · 21.250 kg bruto" },
      ],
      disciplineLabel: "Disciplina Operativa",
      discipline: [
        "La reputación de la marca siempre pesa más que vender una caja adicional.",
        "Cadena de frío, monitoreo de temperatura y trazabilidad tratados como prioridades críticas.",
        "Validación técnica: registros de maduración, refractometría (Brix), pruebas de estabilidad refrigerada.",
        "El volumen aumenta solo tras un desempeño operativo consistente.",
        "KPIs en las dimensiones de producción, calidad, logística, comercial y financiera.",
      ],
      riskLabel: "Conciencia del Riesgo",
      risk: "Un registro de riesgos explícito cubre la disponibilidad de fruta, la variabilidad de calidad, los costos logísticos, el capital de trabajo, la dependencia del transporte y el escalamiento prematuro — el crecimiento se gestiona deliberadamente para proteger la calidad y la confianza del cliente.",
    },
    roadmap: {
      eyebrow: "Hoja de Ruta de Crecimiento",
      heading: { pre: "De la Validación a la ", em: "Integración Vertical" },
      subcopy: "Una visión a largo plazo: una plataforma agrícola integrada — expandible a mango, cacao, limón y aguacate bajo la misma arquitectura.",
      phases: [
        { no: "01", title: "Validación Comercial", body: "1 reefer / semana — validar demanda, logística y rentabilidad." },
        { no: "02", title: "Escalamiento Progresivo", body: "Hacia una base estable de 5 reefers / semana, consolidando hacia 10." },
        { no: "03", title: "Control de la Oferta", body: "Contratos de suministro, exclusividad, relaciones estratégicas con productores." },
        { no: "04", title: "Financiamiento Agrícola", body: "Financiamiento selectivo a productores y compromisos de cosecha." },
        { no: "05", title: "Integración Vertical", body: "Producción e infraestructura propias — control total de la cadena de valor." },
      ],
    },
    contact: {
      eyebrow: "Abre una Conversación",
      heading: { pre: "Construyamos ", em: "confianza." },
      subcopy: "Damos la bienvenida a consultas de importadores, distribuidores e inversionistas. Las relaciones comerciales más valiosas se construyen a través de calidad, consistencia y cumplimiento.",
      directorsLabel: "Directores Ejecutivos",
      directors: ["Alicia Rufino", "Raul Barrios"],
      marketsLabel: "Mercados",
      markets: "Estados Unidos · Caribe · Centroamérica · Europa",
      emailLabel: "Correo",
      email: "info@greengold.export",
      iamLabel: "Soy",
      types: [
        { id: "buyer", label: "Comprador" },
        { id: "investor", label: "Inversionista" },
        { id: "producer", label: "Productor" },
        { id: "other", label: "Otro" },
      ],
      fields: { name: "Nombre completo", email: "Correo", company: "Empresa", country: "País", message: "Mensaje" },
      send: "Enviar Consulta",
      sending: "Enviando…",
      received: "Recibido",
      toastSuccess: "Consulta recibida. Nuestro equipo se pondrá en contacto pronto.",
      toastError: "Algo salió mal. Por favor, inténtalo de nuevo.",
    },
    footer: {
      desc: "Plátanos venezolanos premium para mercados internacionales. Reintroduciendo un origen de excelencia bajo estándares modernos de exportación.",
      navLabel: "Navegar",
      originsLabel: "Orígenes",
      origins: ["Sur del Lago de Maracaibo", "Barinas"],
      tagline: "Calidad que genera confianza.",
      rights: "Todos los derechos reservados.",
      subtag: "Calidad que genera confianza.",
    },
  },
};
